import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const categoryName = article
    ? categories.find((c) => c.slug === article.category)?.name || article.category
    : '';
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <SEOHead
          title="文章未找到 - 凪光在线"
          description="抱歉，您请求的文章不存在或已被移除。"
          canonicalUrl="https://naguflow.github.io"
        />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您请求的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 凪光在线`}
        description={article.summary}
        canonicalUrl={`https://naguflow.github.io/article/${article.slug}`}
        ogType="article"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.publishDate,
            dateModified: article.updateDate,
            url: `https://naguflow.github.io/article/${article.slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://naguflow.github.io',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: categoryName,
                item: `https://naguflow.github.io/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: categoryName, path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="py-10 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    发布于 {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-refresh-line"></i>
                    更新于 {article.updateDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>

              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-semibold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                  [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1
                  [&_li]:text-sm [&_li]:leading-relaxed
                  [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回 {categoryName}
                  </Link>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-500">分享：</span>
                    <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                      <i className="ri-link text-sm"></i>
                    </button>
                    <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                      <i className="ri-weibo-line text-sm"></i>
                    </button>
                    <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                      <i className="ri-wechat-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-16 max-w-3xl mx-auto">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">相关文章推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-2">{related.readTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}